import React, { FC, useMemo, useState } from "react";
import { ModalDialog } from "../../../../components";
import { Icon, RadioGroup, Text } from "@telia/teddy";
import cn from "classnames";
import _reverse from "lodash/reverse";
import _sortBy from "lodash/sortBy";
import { connect } from "react-redux";
import { MAX_NUMBER_OF_ALLOWED_SUBSCRIPTIONS, NEW_ACCOUNT_VALUE } from "../../../../../../constants";
import { RootState } from "../../../../../../models";
import { AccountIdWithType, AccountSubscription, SubscriptionsState } from "../../../../../../models/state";
import { isBroadbandCode } from "../../../../../../shared/subscriptionCodeUtils";
import { getCheckoutSubscriptions } from "../../../state/selectors";
import { getAccountsWithSubscriptions } from "../../../state/selectors/accountSelectors";
import TEXT from "../../../text-consts";
import "./ChooseBanModal.less";

interface StateProps {
    accountIdsWithTypes: AccountIdWithType[];
    checkoutSubscriptions: SubscriptionsState;
}

interface ChooseBanModalProps extends StateProps {
    numberOfCartSubscriptions: number;
    onClose: () => void;
    onContinue: (selectedBan: string) => void;
    shouldShow: boolean;
}

type Subscriptions = { mbb: AccountSubscription[]; phone: AccountSubscription[] };
const getSubscriptions: (account: AccountIdWithType) => Subscriptions = (account) =>
    account.subscriptions.reduce<Subscriptions>(
        (acc, subscription) => {
            if (subscription.subscriptionStatus === "A") {
                if (isBroadbandCode(subscription.offeringCode)) {
                    acc.mbb.push(subscription);
                } else {
                    acc.phone.push(subscription);
                }
            }
            return acc;
        },
        {
            phone: [],
            mbb: [],
        },
    );

const wouldExceedMaxSubscriptions = (account: AccountIdWithType, numberOfCartSubscriptions: number): boolean =>
    account.subscriptions.length + numberOfCartSubscriptions > MAX_NUMBER_OF_ALLOWED_SUBSCRIPTIONS;

const isBanFull = wouldExceedMaxSubscriptions;

const ChooseBanModal: FC<ChooseBanModalProps> = ({
    accountIdsWithTypes,
    checkoutSubscriptions,
    numberOfCartSubscriptions,
    shouldShow,
    onContinue,
    onClose,
}) => {
    const [selectedBan, setSelectedBan] = useState("merge");
    const hasOneSubscriptionInCheckout = Object.keys(checkoutSubscriptions).length === 1;

    const sorted = useMemo(
        () =>
            _reverse(
                _sortBy(accountIdsWithTypes, [
                    "subscriptions.length",
                    (account): number =>
                        account.subscriptions.some((subscription) => isBroadbandCode(subscription.offeringCode))
                            ? -1
                            : 1,
                ]),
            ),
        [accountIdsWithTypes],
    );

    return shouldShow ? (
        <ModalDialog
            size="medium"
            name="choose-ban-modal"
            closeText="Avbryt"
            submitText="Ok"
            submitKind="primary"
            closeKind="secondary"
            heading={TEXT.PERSONAL_DETAILS.CHOOSE_BAN.HEADING}
            className="ban-modal-dialog"
            onClose={(): void => onClose()}
            onSubmit={(): void => onContinue(selectedBan)}
        >
            <RadioGroup value={selectedBan} onValueChange={setSelectedBan} name="radiobutton-group-select-ban">
                <RadioGroup.Item>
                    <RadioGroup.Trigger value="merge" />
                    <RadioGroup.Label>
                        <Text variant="additional-100">Slå sammen alle fakturaer til én faktura</Text>
                    </RadioGroup.Label>
                </RadioGroup.Item>
            </RadioGroup>
            <Text mb="100" variant="additional-100">
                {TEXT.PERSONAL_DETAILS.CHOOSE_BAN.DESCRIPTION(hasOneSubscriptionInCheckout)}
            </Text>
            <div className="ban-modal">
                {sorted.map((account, index) => {
                    const subscriptions = getSubscriptions(account);
                    const selectedClass = selectedBan === account.accountId ? "selected" : "";
                    const noActiveSubscriptions = subscriptions.phone.length === 0 && subscriptions.mbb.length === 0;
                    const isDisabled = noActiveSubscriptions || isBanFull(account, numberOfCartSubscriptions);

                    return (
                        <div
                            className={cn("ban-modal__ban", {
                                selected: selectedBan === account.accountId,
                                disabled: isDisabled,
                            })}
                            key={index}
                            onClick={(): void => {
                                if (!isDisabled) {
                                    setSelectedBan(account.accountId);
                                }
                            }}
                        >
                            <div className="ban-modal__ban-header">
                                <div className="ban-modal__header-text" data-di-mask>
                                    <Icon size="lg" name="doc" />
                                    {TEXT.MODAL.BAN.BAN_HEADING} {account.accountId}
                                </div>
                                <Icon name="checkmark" className={`ban-modal__check-mark ${selectedClass}`} />
                            </div>
                            <div>
                                {subscriptions.phone.length || subscriptions.mbb.length ? (
                                    <>
                                        {subscriptions.phone.length ? (
                                            <div className="ban-modal__subsription-content">
                                                <div className="ban-modal__subscription-heading">
                                                    {TEXT.MODAL.BAN.SUBSCRIPTION_HEADING}
                                                </div>
                                                <ul data-di-mask>
                                                    {subscriptions.phone.map(({ name, phoneNumber }) => (
                                                        <li key={phoneNumber}>
                                                            {phoneNumber}
                                                            {", "}
                                                            <span className="ban-modal__subscription-type">{name}</span>
                                                        </li>
                                                    ))}
                                                </ul>
                                            </div>
                                        ) : null}

                                        {subscriptions.mbb.length ? (
                                            <div className="ban-modal__subsription-content">
                                                <div className="ban-modal__subscription-heading">
                                                    {TEXT.MODAL.BAN.MBB_HEADING}
                                                </div>
                                                <ul data-di-mask>
                                                    {subscriptions.mbb.map(({ name, phoneNumber }) => (
                                                        <li key={phoneNumber}>{name}</li>
                                                    ))}
                                                </ul>
                                            </div>
                                        ) : null}
                                    </>
                                ) : (
                                    `Ingen aktive abonnement`
                                )}
                            </div>
                        </div>
                    );
                })}
                <div
                    className={cn("ban-modal__ban", "separate-ban", {
                        selected: selectedBan === NEW_ACCOUNT_VALUE,
                    })}
                    onClick={(): void => {
                        setSelectedBan(NEW_ACCOUNT_VALUE);
                    }}
                >
                    <Icon
                        name="checkmark"
                        className={cn("ban-modal__check-mark-separate", {
                            selected: selectedBan === NEW_ACCOUNT_VALUE,
                        })}
                    />
                    <Icon name="add" className="ban-modal__add-icon" />
                    <span className="ban-modal__separate-ban-text">Jeg vil ha egen faktura</span>
                </div>
            </div>
        </ModalDialog>
    ) : null;
};

const mapStateToProps: (state: RootState) => StateProps = (state) => ({
    accountIdsWithTypes: getAccountsWithSubscriptions(state),
    checkoutSubscriptions: getCheckoutSubscriptions(state),
});

export default connect(mapStateToProps)(ChooseBanModal);
